<template>
  <div class="to-email-input">
    <div class="input-section">
      <span class="label">TO</span>
      <div>
        <el-select
          ref="toEmailInputSelect"
          v-model="internalToEmailAsArray"
          :class="{'hide-input' : internalToEmailAsArray.length > 0}"
          :filter-method="debounceHandleToInputValue"
          :teleported="false"
          :popper-options="popperOptions"
          :loading="isLoadingCustomers"
          :popper-class="popperClass"
          loading-text="..."
          filterable
          allow-create
          placeholder=""
          reserve-keyword
          multiple
          @keyup.tab="handleToDelimiter"
          @keyup.enter="handleToDelimiter"
          @visible-change="handleToVisibleChange"
          @change="clearInputFields"
        >
          <el-option
            v-for="customer in filteredAvailableCustomers"
            :key="customer.id"
            :value="customer.email"
            :label="getToLabel(customer)"
          >
            <span>
              <avatar :person="customer" :size="18" />
              <span v-html="getOptionsHtmlLabel(customer)" />
            </span>
          </el-option>
        </el-select>
      </div>
    </div>
    <dd-button type="text" size="tiny" @click="$emit('update-canceled')">Cancel</dd-button>
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import { debounce } from 'lodash-es'
import { ClickOutside as Clickoutside } from 'element-plus'

/* Mixins */

/* Components */

/* Helpers */
import TextAssistant from '@/helpers/text-assistant'
import PopperAssistant from '@/helpers/popper-assistant'

/* Data types */
import { IPersonWithEmail } from '@/api/types/response.types'

/* API */
import MailboxesAPI from '@/api/modules/mailboxes.api'

/* 
  This component is used for entering email addresses for conversations. It will load all existing
  customers from the passed-in mailbox ID so a user can select from the list (or type in their own)
*/
/* The parts of el-select's instance that this component works with */
interface ISelectInternals {
  states: { inputValue: string }
  selectionRef: HTMLElement
}

export default defineComponent({
  name: 'ToEmailInput',

  directives: { Clickoutside },

  props: {
    mailboxId: {
      type: Number,
      required: true,
    },
    toEmail: {
      type: String,
      required: true,
    },
  },

  emits: ['update-canceled', 'update-to-email', 'update:toEmail'],

  data() {
    return {
      // Fixed inside dialogs and drawers, so their scrolling body doesn't cut the dropdown off
      popperOptions: {},
      isLoadingCustomers: false,
      shouldRefreshAvailableCustomers: true,

      availableCustomers: [] as IPersonWithEmail[],
      filteredAvailableCustomers: [] as IPersonWithEmail[],

      curToInputValue: '',
      debounceHandleToInputValue: null as any,
    }
  },

  computed: {
    internalToEmail: {
      get(): string {
        return this.toEmail
      },
      set(val: string) {
        if (!TextAssistant.isValidEmail(val)) {
          val = ''
          // @ts-ignore
          this.$refs.toEmailInputSelect.focus()
        }
        /* Can use sync or custom 'update-to-email' event to handle toEmail update */
        this.$emit('update:toEmail', val)
        this.$emit('update-to-email', val)
      },
    },
    internalToEmailAsArray: {
      get(): Array<string> {
        return this.toEmail.length > 0 ? [this.toEmail] : []
      },
      set(val: Array<string>) {
        this.internalToEmail = val.length > 0 ? val[0] : ''
      },
    },
    popperClass(): string {
      let result = 'options-dropdown '

      if (this.filteredAvailableCustomers.length === 0) {
        result += 'remove'
      }
      return result
    },
  },

  async created() {
    this.debounceHandleToInputValue = debounce(this.handleToInputValue, 250)
    await this.$nextTick()
    // @ts-ignore
    this.$refs.toEmailInputSelect.focus()
  },

  mounted() {
    this.popperOptions = PopperAssistant.inPlacePopperOptions(this.$el)
    this.addInputPlaceholders()

    this.select().selectionRef.addEventListener(
      'click',
      (event: MouseEvent) => {
        event.stopPropagation()
      },
    )
  },

  methods: {
    select(): ISelectInternals {
      return this.$refs.toEmailInputSelect as unknown as ISelectInternals
    },

    getToLabel(customer: IPersonWithEmail): string {
      if (customer.name !== customer.email) {
        return customer.name + ' <' + customer.email + '>'
      }
      return customer.email
    },
    getOptionsHtmlLabel(customer: IPersonWithEmail): string {
      if (customer.name !== customer.email) {
        return (
          '<strong>' +
          customer.name +
          '</strong>&nbsp;&lt;' +
          customer.email +
          '&gt;'
        )
      }
      return customer.email
    },
    addInputPlaceholders() {
      const toInput: HTMLInputElement = this.$el.querySelector(
        '.el-select__input',
      )

      toInput.placeholder = 'Enter to address...'
    },

    async filterAvailableCustomers(val: string) {
      if (this.$screen.isMobile(this.$mq)) {
        return
      }
      if (val.trim().length <= 1) {
        this.filteredAvailableCustomers = []
        return
      }
      // Skip load/prune if we are still in the customer API call...
      if (!this.isLoadingCustomers) {
        if (this.shouldRefreshAvailableCustomers) {
          try {
            this.isLoadingCustomers = true
            this.availableCustomers = await MailboxesAPI.get_Customers(
              this.mailboxId,
            )
            this.isLoadingCustomers = false
            this.shouldRefreshAvailableCustomers = false
          } catch (error) {
            this.$error.emit(error)
          }
        }

        const loweredVal = val.toLowerCase()

        this.filteredAvailableCustomers = this.availableCustomers
          .filter(
            i =>
              i.email.includes(loweredVal) ||
              i.name.toLowerCase().includes(loweredVal),
          )
          .slice(0, 200)
      }
    },

    attemptToEmailPush(val: string) {
      if (TextAssistant.isValidEmail(val)) {
        this.internalToEmail = val
      }
      this.clearInputFields()
    },

    clearInputFields() {
      this.curToInputValue = ''
      this.select().states.inputValue = ''
      this.filterAvailableCustomers('')
    },

    async handleToInputValue(val: string) {
      /* Store the current value separately. This will be used
      in case user focuses out of the select field so we can evaluate
      whether the last typed-in value needs to be processed. Otherwise,
      the select's query is empty whenever we
      lose focus*/
      this.curToInputValue = val
      if (val.trim().length < 1) {
        return
      }

      // Skip load/prune if we are still in the customer API call...
      await this.filterAvailableCustomers(val)

      // Treat comma as a delimiter
      if (val.endsWith(',')) {
        const inputNoComma = val.slice(0, -1)
        // Checking if includes comma because if you just hold "," sometimes
        // handleCurInputValue will pass a value of some number of commas because of debouncing
        if (inputNoComma !== '' && !inputNoComma.includes(',')) {
          this.attemptToEmailPush(inputNoComma)
        }
      }
    },

    handleToDelimiter() {
      const inputValue = this.select().states.inputValue.trim()
      this.attemptToEmailPush(inputValue)
    },

    handleToVisibleChange(value: boolean) {
      /* If we are clicking out of a non-search only picker, 
         process any remaining value in the input. */
      if (!value) {
        this.attemptToEmailPush(this.curToInputValue)
      }
    },
  },
})
</script>

<style lang="scss">
.to-email-input {
  display: flex;
  align-items: center;

  @include media-breakpoint-down(sm) {
    flex-direction: column;
    align-items: flex-end;
    gap: 12px;
  }
  > .input-section {
    border-radius: 40px;
    height: 30px;
    width: 400px;

    @include media-breakpoint-down(sm) {
      width: 100%;
    }

    border: solid 1px $color--gray-30;
    display: flex;
    align-items: center;
    color: var(--color-text-secondary);
    padding: 0 10px;

    > .label {
      display: inline-block;
      @include lettering--h5;
      width: 12px;
    }

    > div {
      display: flex;
      flex: 1 1 auto;
      border-top: 1px solid transparent;
      border-right: 1px solid transparent;
      border-bottom: 1px solid transparent;

      @include media-breakpoint-down(sm) {
        overflow: hidden;
        overflow-x: auto;
      }
    }

    .hide-input {
      .el-select__input,
      .el-select__popper {
        display: none;
      }
    }

    .el-select {
      flex: 1 1 auto;

      &__input {
        margin-left: 10px;
        min-width: 350px;
        @include lettering--p-sm;

        @include media-breakpoint-down(sm) {
          min-width: auto;
        }
      }

      /* Element Plus hides an empty filter input until the select is focused, but ours holds the placeholder */
      &__input-wrapper.is-hidden {
        position: static;
        opacity: 1;
      }

      &__selection {
        max-width: 100% !important;

        /* Element Plus pulls the tags 8px into padding this select doesn't have; Element UI gave them a 6px margin */
        &.is-near {
          margin-left: 6px;
        }
      }

      /* Only the tags and the filter input show: the select's own box is invisible */
      .el-select__wrapper,
      .el-select__wrapper.is-focused,
      .el-select__wrapper:hover {
        padding: 0;
        font-size: 15px;
        background-color: transparent;
        box-shadow: none;
        transition: none;
      }

      // Don't show caret
      .el-select__suffix {
        display: none;
      }

      .el-select__selection .el-tag {
        display: flex;
        align-items: center;
        padding: 1px 12px 0 12px;
        background-image: none;
        border: 0 !important;
        border-radius: 14px !important;

        &:hover {
          background-color: $color--gray-30;
        }
      }

      /* The dropdown class is on the select's popper */
      .el-popper.options-dropdown {
        min-width: 300px !important;
        max-width: 328px !important;

        &.absolute {
          position: absolute !important;
          top: 40px !important;
        }

        @include media-breakpoint-up(md) {
          max-width: 400px !important;
        }
      }

      &-dropdown__item {
        display: flex;
        align-items: center;
      }
    }

    .button {
      font-size: 16px;
      color: var(--color-text-third);
      text-decoration: none;
    }

    .ddi {
      top: 0;
      font-size: 0;
      line-height: 0;
      color: var(--color-text-third);

      &::before {
        width: 0;
        height: 0;
        font-size: 16px !important;
        line-height: 16px !important;
      }
    }
  }

  .options-dropdown {
    min-width: 100%;
    max-width: 100%;
    margin-left: 0;

    &.remove {
      display: none;
    }

    @include media-breakpoint-down(sm) {
      visibility: hidden;
    }
  }
}
</style>
